// Enhanced caching system combining memory, persistent, and HTTP caching
import { memoryCache, CACHE_KEYS, CACHE_TTL } from './cache';
import { persistentCache, PERSISTENT_CACHE_TTL } from './redis-cache';

// Cache strategy configuration
const CACHE_STRATEGIES = {
  CACHE_FIRST: 'cache-first',
  NETWORK_FIRST: 'network-first',
  STALE_WHILE_REVALIDATE: 'stale-while-revalidate'
} as const;

type CacheStrategy = typeof CACHE_STRATEGIES[keyof typeof CACHE_STRATEGIES];

// Cache levels: Memory -> IndexedDB -> Network
export class EnhancedCache {
  // Get data with configurable caching strategy
  static async get<T>(
    key: string, 
    fallbackFn: () => Promise<T>, 
    ttl: number = CACHE_TTL.CONTENT_LIST,
    strategy: CacheStrategy = CACHE_STRATEGIES.CACHE_FIRST
  ): Promise<T> {
    try {
      if (strategy === CACHE_STRATEGIES.CACHE_FIRST) {
        return await this.getCacheFirst(key, fallbackFn, ttl);
      } else if (strategy === CACHE_STRATEGIES.STALE_WHILE_REVALIDATE) {
        return await this.getStaleWhileRevalidate(key, fallbackFn, ttl);
      } else {
        return await this.getNetworkFirst(key, fallbackFn, ttl);
      }
    } catch (error) {
      console.error('🚨 Enhanced Cache: Error for', key, error);
      throw error;
    }
  }

  // Cache-first strategy (best for stable content)
  private static async getCacheFirst<T>(key: string, fallbackFn: () => Promise<T>, ttl: number): Promise<T> {
    // Level 1: Memory cache (fastest)
    const memoryResult = memoryCache.get<T>(key);
    if (memoryResult) {
      console.log('⚡ Enhanced Cache: Memory hit for', key);
      return memoryResult;
    }

    // Level 2: Persistent cache (IndexedDB)
    const persistentResult = await persistentCache.get<T>(key);
    if (persistentResult) {
      console.log('💾 Enhanced Cache: Persistent hit for', key);
      // Store in memory for next time
      memoryCache.set(key, persistentResult, ttl);
      return persistentResult;
    }

    // Level 3: Network fallback
    console.log('🌐 Enhanced Cache: Network fallback for', key);
    const networkResult = await fallbackFn();
    
    // Store in both caches
    memoryCache.set(key, networkResult, ttl);
    await persistentCache.set(key, networkResult, ttl * 3); // Longer TTL for persistent
    
    return networkResult;
  }

  // Network-first strategy (for fresh data)
  private static async getNetworkFirst<T>(key: string, fallbackFn: () => Promise<T>, ttl: number): Promise<T> {
    try {
      // Try network first
      console.log('🌐 Enhanced Cache: Network first for', key);
      const networkResult = await fallbackFn();
      
      // Store in both caches
      memoryCache.set(key, networkResult, ttl);
      await persistentCache.set(key, networkResult, ttl * 2);
      
      return networkResult;
    } catch (error) {
      console.log('📦 Enhanced Cache: Network failed, trying cache for', key);
      
      // Level 1: Memory cache (fastest)
      const memoryResult = memoryCache.get<T>(key);
      if (memoryResult) {
        console.log('⚡ Enhanced Cache: Memory fallback hit for', key);
        return memoryResult;
      }

      // Level 2: Persistent cache (IndexedDB)
      const persistentResult = await persistentCache.get<T>(key);
      if (persistentResult) {
        console.log('💾 Enhanced Cache: Persistent fallback hit for', key);
        // Store in memory for next time
        memoryCache.set(key, persistentResult, ttl);
        return persistentResult;
      }
      
      throw error;
    }
  }

  // Stale-while-revalidate strategy (best UX)
  private static async getStaleWhileRevalidate<T>(key: string, fallbackFn: () => Promise<T>, ttl: number): Promise<T> {
    // Level 1: Memory cache (fastest)
    const memoryResult = memoryCache.get<T>(key);
    if (memoryResult) {
      console.log('⚡ Enhanced Cache: Memory hit (stale-while-revalidate) for', key);
      
      // Background revalidation (don't await)
      this.backgroundRevalidate(key, fallbackFn, ttl).catch(error => {
        console.warn('🔄 Background revalidation failed for', key, error);
      });
      
      return memoryResult;
    }

    // Level 2: Persistent cache (IndexedDB)
    const persistentResult = await persistentCache.get<T>(key);
    if (persistentResult) {
      console.log('💾 Enhanced Cache: Persistent hit (stale-while-revalidate) for', key);
      // Store in memory for next time
      memoryCache.set(key, persistentResult, ttl);
      
      // Background revalidation (don't await)
      this.backgroundRevalidate(key, fallbackFn, ttl).catch(error => {
        console.warn('🔄 Background revalidation failed for', key, error);
      });
      
      return persistentResult;
    }

    // Level 3: Network (no cache available)
    console.log('🌐 Enhanced Cache: Network (no cache) for', key);
    const networkResult = await fallbackFn();
    
    // Store in both caches
    memoryCache.set(key, networkResult, ttl);
    await persistentCache.set(key, networkResult, ttl * 3);
    
    return networkResult;
  }

  // Background revalidation helper
  private static async backgroundRevalidate<T>(key: string, fallbackFn: () => Promise<T>, ttl: number): Promise<void> {
    try {
      console.log('🔄 Background revalidating', key);
      const networkResult = await fallbackFn();
      
      // Store in both caches
      memoryCache.set(key, networkResult, ttl);
      await persistentCache.set(key, networkResult, ttl * 3);
      
      console.log('✅ Background revalidation complete for', key);
    } catch (error) {
      console.warn('⚠️ Background revalidation failed for', key, error);
      // Don't throw - this is background operation
    }
  }

  // Set data in all cache levels
  static async set<T>(key: string, data: T, ttl: number = CACHE_TTL.CONTENT_LIST): Promise<void> {
    try {
      memoryCache.set(key, data, ttl);
      await persistentCache.set(key, data, ttl * 2);
      console.log('💾 Enhanced Cache: Stored', key);
    } catch (error) {
      console.error('🚨 Enhanced Cache: Failed to store', key, error);
    }
  }

  // Delete from all cache levels
  static async delete(key: string): Promise<void> {
    try {
      memoryCache.delete(key);
      await persistentCache.delete(key);
      console.log('🗑️ Enhanced Cache: Deleted', key);
    } catch (error) {
      console.error('🚨 Enhanced Cache: Failed to delete', key, error);
    }
  }

  // Clear all caches
  static async clear(): Promise<void> {
    try {
      memoryCache.clear();
      await persistentCache.clear();
      console.log('🧹 Enhanced Cache: Cleared all caches');
    } catch (error) {
      console.error('🚨 Enhanced Cache: Failed to clear caches', error);
    }
  }

  // Preload critical data
  static async preloadCriticalData(country: string): Promise<void> {
    try {
      console.log('🚀 Enhanced Cache: Preloading critical data for', country);
      
      // Preload streaming platforms (rarely changes)
      const platformsKey = CACHE_KEYS.STREAMING_PLATFORMS;
      await this.get(platformsKey, async () => {
        const { supabase } = await import('./supabase');
        const { data } = await supabase.from('streaming_platforms').select('*');
        return data || [];
      }, PERSISTENT_CACHE_TTL.STREAMING_PLATFORMS, CACHE_STRATEGIES.CACHE_FIRST);

      // Preload recent content for country
      const contentKey = CACHE_KEYS.CONTENT_LIST(country);
      await this.get(contentKey, async () => {
        const { getCachedContent } = await import('./supabase');
        return await getCachedContent(country, 20);
      }, PERSISTENT_CACHE_TTL.CONTENT_LIST, CACHE_STRATEGIES.STALE_WHILE_REVALIDATE);

      console.log('✅ Enhanced Cache: Critical data preloaded');
    } catch (error) {
      console.error('🚨 Enhanced Cache: Failed to preload critical data', error);
    }
  }
}

// HTTP Cache headers utility
export class HTTPCacheHeaders {
  static getHeaders(maxAge: number, staleWhileRevalidate: number = maxAge / 2): Record<string, string> {
    return {
      'Cache-Control': `public, max-age=${maxAge}, stale-while-revalidate=${staleWhileRevalidate}`,
      'Expires': new Date(Date.now() + maxAge * 1000).toUTCString(),
      'Last-Modified': new Date().toUTCString(),
      'ETag': `"${Date.now()}"`,
    };
  }

  static getStaticAssetHeaders(): Record<string, string> {
    // 1 year cache for static assets
    return this.getHeaders(31536000, 86400); // 1 year max-age, 1 day stale-while-revalidate
  }

  static getAPIHeaders(): Record<string, string> {
    // 1 hour cache for API responses
    return this.getHeaders(3600, 1800); // 1 hour max-age, 30 minutes stale-while-revalidate
  }

  static getImageHeaders(): Record<string, string> {
    // 1 week cache for images
    return this.getHeaders(604800, 86400); // 1 week max-age, 1 day stale-while-revalidate
  }
}

// Initialize enhanced caching
if (typeof window !== 'undefined') {
  // Initialize persistent cache early
  persistentCache.init().then(() => {
    console.log('✅ Persistent cache initialized');
    
    // Preload critical data after cache is ready
    const storedCountry = localStorage.getItem('selectedCountry') || 'US';
    EnhancedCache.preloadCriticalData(storedCountry);
  }).catch(error => {
    console.warn('⚠️ Persistent cache initialization failed:', error);
    
    // Still preload with memory cache only
    const storedCountry = localStorage.getItem('selectedCountry') || 'US';
    EnhancedCache.preloadCriticalData(storedCountry);
  });
}